import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Map as MapIcon } from 'lucide-react';
import { CompanyGeoSummaryResponse, CompanyGeoRegency } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import indonesiaTopo from '../assets/indonesia-provinces.topo.json';
import { TOPO_NAME_PROP, topoToDb, dbToTopo } from '../data/provinceNameMap';

const fillForCount = (count: number, max: number): string => {
  if (count <= 0) return '#e5e7eb'; // gray-200
  const t = max > 0 ? count / max : 0;
  if (t > 0.66) return '#1d4ed8'; // blue-700
  if (t > 0.33) return '#3b82f6'; // blue-500
  return '#93c5fd'; // blue-300
};

const CompanyMap: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<CompanyGeoSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProvinsi, setSelectedProvinsi] = useState<string | null>(null);
  const [hover, setHover] = useState<{ name: string; count: number } | null>(null);
  const [activelyUsing, setActivelyUsing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setData(await api.getCompanyGeoSummary(activelyUsing));
      } catch (e) {
        console.error('Error loading geo summary:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [activelyUsing]);

  const countByProvinsi = useMemo(() => {
    const m: Record<string, number> = {};
    (data?.provinces ?? []).forEach((p) => { m[p.provinsi] = p.companiesCount; });
    return m;
  }, [data]);

  const maxProvinceCount = useMemo(
    () => (data?.provinces ?? []).reduce((mx, p) => Math.max(mx, p.companiesCount), 0),
    [data]
  );

  // DB provinces present in the data but not mappable to a topo feature — surface, never hide.
  const unmappedProvinces = useMemo(
    () => (data?.provinces ?? []).filter((p) => !dbToTopo[p.provinsi]),
    [data]
  );

  const regencyRows: CompanyGeoRegency[] = useMemo(() => {
    const all = data?.regencies ?? [];
    if (selectedProvinsi) {
      return all.filter((r) => r.provinsi === selectedProvinsi); // already count-desc from API
    }
    return all.slice(0, 50); // top 50 nationally
  }, [data, selectedProvinsi]);

  const maxRegencyCount = regencyRows.reduce((mx, r) => Math.max(mx, r.companiesCount), 0) || 1;

  const goToProvince = (provinsi: string) => {
    navigate(`/companies?provinsi=${encodeURIComponent(provinsi)}&page=1${activelyUsing ? '&isActivelyUsing=1' : ''}`);
  };
  const goToRegency = (r: CompanyGeoRegency) => {
    navigate(`/companies?provinsi=${encodeURIComponent(r.provinsi)}&kabupaten=${encodeURIComponent(r.kabupaten)}&page=1${activelyUsing ? '&isActivelyUsing=1' : ''}`);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-2">
        <MapIcon className="h-6 w-6 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">Company Map</h1>
      </div>
      <p className="text-sm text-gray-500 mb-3">
        {(data?.meta.totalWithAddress ?? 0).toLocaleString()} dari {(data?.meta.totalCompanies ?? 0).toLocaleString()} company punya alamat
      </p>

      <div className="flex items-center gap-2 mb-6">
        <input
          type="checkbox"
          id="activelyUsing"
          checked={activelyUsing}
          onChange={(e) => setActivelyUsing(e.target.checked)}
          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="activelyUsing" className="text-sm font-medium text-gray-700 cursor-pointer">
          Sedang aktif dipakai
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Map */}
        <div className="bg-white rounded-lg shadow p-4 relative">
          {hover && (
            <div className="absolute top-3 left-3 z-10 bg-gray-900 text-white text-xs rounded px-2 py-1">
              {hover.name}: {hover.count} company
            </div>
          )}
          <ComposableMap projection="geoMercator" projectionConfig={{ scale: 1100, center: [118, -2] }} height={420}>
            <Geographies geography={indonesiaTopo as any}>
              {({ geographies }: any) =>
                geographies.map((geo: any) => {
                  const topoName = geo.properties[TOPO_NAME_PROP];
                  const dbName = topoToDb[topoName];
                  const count = dbName ? (countByProvinsi[dbName] ?? 0) : 0;
                  const isSelected = dbName && dbName === selectedProvinsi;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setHover({ name: dbName ?? topoName, count })}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => dbName && setSelectedProvinsi(dbName)}
                      style={{
                        default: { fill: fillForCount(count, maxProvinceCount), stroke: isSelected ? '#111827' : '#ffffff', strokeWidth: isSelected ? 1.5 : 0.5, outline: 'none' },
                        hover: { fill: '#2563eb', stroke: '#111827', strokeWidth: 1, outline: 'none', cursor: 'pointer' },
                        pressed: { fill: '#1d4ed8', outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#e5e7eb' }} /> 0</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#93c5fd' }} /> rendah</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#3b82f6' }} /> sedang</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#1d4ed8' }} /> tinggi</span>
          </div>
        </div>

        {/* Regency panel */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">
              {selectedProvinsi ?? 'Top kabupaten (nasional)'}
            </h2>
            {selectedProvinsi && (
              <button onClick={() => goToProvince(selectedProvinsi)} className="text-sm text-primary-600 hover:text-primary-800">
                Lihat semua di {selectedProvinsi} →
              </button>
            )}
          </div>
          {selectedProvinsi && (
            <button onClick={() => setSelectedProvinsi(null)} className="mb-3 text-xs text-gray-500 hover:text-gray-700">
              ← kembali ke top nasional
            </button>
          )}
          <div className="divide-y">
            {regencyRows.map((r) => (
              <button
                key={`${r.provinsi}__${r.kabupaten}`}
                onClick={() => goToRegency(r)}
                className="w-full text-left py-2 hover:bg-gray-50 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-900 truncate">
                      {r.kabupaten}{!selectedProvinsi && <span className="text-gray-400"> · {r.provinsi}</span>}
                    </span>
                    <span className="ml-2 text-sm font-semibold text-primary-700">{r.companiesCount}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-primary-500" style={{ width: `${(r.companiesCount / maxRegencyCount) * 100}%` }} />
                  </div>
                </div>
              </button>
            ))}
            {regencyRows.length === 0 && (
              <div className="py-8 text-center text-gray-500 text-sm">Belum ada company dengan alamat di area ini.</div>
            )}
          </div>

          {unmappedProvinces.length > 0 && (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Provinsi tidak terpetakan di peta (tetap dihitung): {unmappedProvinces.map((p) => `${p.provinsi} (${p.companiesCount})`).join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyMap;
